import React, { useEffect, useRef, useState } from 'react'
import { sendGmailEmail, EmailRecipient, EmailAttachment } from '../../utils/gmailUtils';
import { useAuth } from '../../AuthContext';
import { collection, doc, getDoc, getDocs, query, Timestamp, addDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import Toast from '../Toast';
import { Calendar, Mail, Search } from 'lucide-react';
import GradientSubmitButton from '../GradientSubmitButton';



interface EmailModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedStudent?: {
        id: string;
        name: string;
        phone: string;
    };
    subject?: string;
    body?: string;
    files?: EmailAttachment[];
    isHtml?: boolean;
}

interface EmailTemplate {
    id?: string;
    name: string;
    subject: string;
    content: string;
    category: string;
    createdAt: Date;
    lastModified: Date;
}

const EmailModal = (
    { isOpen, onClose, selectedStudent, body, subject, files, isHtml }: EmailModalProps
) => {

    // Auth Context
    const { currentUser, userData, isAdmin, isManager, isStaff } = useAuth();

    // States
    const [recipients, setRecipients] = useState<EmailRecipient[]>([]);
    const [manualRecipient, setManualRecipient] = useState(false);
    const [recipientEmail, setRecipientEmail] = useState('');
    const [emailSubject, setEmailSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');

    const [attachments, setAttachments] = useState<EmailAttachment[]>([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const emailInputRef = useRef<HTMLInputElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [sendLoading, setSendLoading] = useState(false);

    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    // File to base64 conversion utility
    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                // Extract the base64 part from the data URL
                const base64String = reader.result as string;
                // Remove the "data:mimetype;base64," prefix
                const base64Content = base64String.split(',')[1];
                resolve(base64Content);
            };
            reader.onerror = error => reject(error);
        });
    };

    useEffect(() => {
        // Set email body
        if (body !== undefined) {
            setEmailBody(body.trim() === '' ? '' : body);
        }

        // Set email subject
        if (subject !== undefined) {
            setEmailSubject(subject.trim() === '' ? '' : subject);
        }

        // Set attachments if provided
        if (files && files.length > 0) {
            setAttachments(files);
        }
    }, [body, subject, files]);

    // Function to find the email of each selected ID from leads subcollection in companies collection
    useEffect(() => {
        const fetchEmails = async () => {
            try {
                const id = selectedStudent ? selectedStudent.id : '';
                if (!id) {
                    console.error('No ID provided');
                    setManualRecipient(true);
                    return;
                }
                const emails: string[] = [];
                const docRef = doc(db, `companies/${userData?.companyId}/leads`, id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    if (data && data.email) {
                        emails.push(data.email);
                    }
                }
                console.log('Fetched Emails:', emails);
                const uniqueEmails = Array.from(new Set(emails));
                const emailRecipients: EmailRecipient[] = uniqueEmails.map(email => ({
                    email
                }));
                setRecipients(emailRecipients);
                console.log('Unique Emails:', uniqueEmails);
                console.log('Email Recipients:', emailRecipients);

                // If no emails found, set manual recipient to true
                if (emailRecipients.length === 0) {
                    setManualRecipient(true);
                }
            } catch (error) {
                console.error('Error fetching emails:', error);
            }
        };
        fetchEmails();
    }, [selectedStudent]);

    // Function to handle manual recipient on clicking enter key
    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleManualRecipient();
        }
    }

    // Function to handle manual recipient
    const handleManualRecipient = () => {
        const newEmail = recipientEmail.trim();
        if (!newEmail) {
            setToastMessage("Please enter a valid email address");
            setShowToast(true);
            return;
        }
        // Check if the email is already in the list
        if (recipients.some(recipient => recipient.email === newEmail)) {
            setToastMessage("Email already added");
            setShowToast(true);
            return;
        }
        setRecipients([...recipients, { email: newEmail }]);
        setRecipientEmail('');
        setManualRecipient(false);
    };

    const handleIconClick = () => {
        // Trigger the hidden file input when icon is clicked
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileSelection = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;

        if (files && files.length > 0) {
            setUploading(true);
            try {
                // Convert all files in parallel and collect results
                const newAttachments: EmailAttachment[] = await Promise.all(
                    Array.from(files).map(async (file) => {
                        // Optional size check (10MB example limit)
                        if (file.size > 10 * 1024 * 1024) {
                            setToastMessage(`File ${file.name} exceeds the 10MB size limit`);
                            setShowToast(true);
                            throw new Error(`File ${file.name} exceeds the 10MB size limit`);
                        }

                        // Optional file type check
                        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
                        if (!allowedTypes.includes(file.type)) {
                            console.warn(`File type ${file.type} might not be supported`);
                            // Continue anyway or throw error based on your requirements
                        }

                        const base64Content = await fileToBase64(file);
                        return {
                            filename: file.name,
                            content: base64Content,
                            mimeType: file.type,
                        };
                    })
                );

                // Update state once with all new attachments
                setAttachments((prev) => [...prev, ...newAttachments]);

                setToastMessage(`Added ${newAttachments.length} attachment${newAttachments.length > 1 ? 's' : ''}`);
                setShowToast(true);
            } catch (error: any) {
                console.error("Error processing files:", error);
                setToastMessage(`Error processing files: ${error.message}`);
                setShowToast(true);
            } finally {
                setUploading(false);
            }
        }

        // Reset the file input value
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleAttachmentRemove = (index: number) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index));
    };

    // Function to handle send email
    const handleSendEmail = async () => {
        // Comprehensive validation before sending
        if (recipients.length === 0) {
            setToastMessage("Please add at least one recipient");
            setShowToast(true);
            return;
        }

        if (!emailSubject.trim()) {
            setToastMessage("Please add an email subject");
            setShowToast(true);
            return;
        }

        if (!emailBody.trim()) {
            setToastMessage("Please add email body content");
            setShowToast(true);
            return;
        }

        setSendLoading(true);
        try {

            // Calculate total attachment size
            const totalSize = attachments.reduce((size, attachment) => {
                // Base64 string length * 0.75 is approximately the decoded size in bytes
                const decodedSize = attachment.content.length * 0.75;
                return size + decodedSize;
            }, 0);

            // Gmail has a 25MB attachment limit
            if (totalSize > 25 * 1024 * 1024) {
                setToastMessage("Total attachment size exceeds 25MB limit");
                setShowToast(true);
                throw new Error("Total attachment size exceeds 25MB limit");
            }

            console.log('Sending email with the following details:', {
                recipients,
                subject: emailSubject,
                body: '\n' + emailBody,
                attachments: attachments
            });


            const result = await sendGmailEmail({
                to: recipients,
                subject: emailSubject,
                body: '\n' + emailBody.trim(), // Remove the newline prefix
                attachments: attachments,
                isHtml: isHtml,
            });

            console.log('Email sent successfully:', result);

            // if response is true then store the email info in database in emails collection
            if (result) {
                const emailData = {
                    subject: emailSubject,
                    to: recipients.map(recipient => recipient.email),
                    createdAt: Timestamp.now(),
                    userId: currentUser?.uid,
                    leadId: selectedStudent ? selectedStudent.id : '',
                }

                // Store email in Firestore
                const emailRef = collection(db, `companies/${userData?.companyId}/emails`);
                await addDoc(emailRef, emailData);
                console.log('Email stored in Firestore:', emailData);
            }

            // Clear form after successful send if desired
            setEmailSubject('');
            setEmailBody('');
            setAttachments([]);

            setToastMessage("Email sent successfully");
            setShowToast(true);
            
            // Close the modal after a short delay to show the success message
            setTimeout(() => {
                onClose();
            }, 2000);
        } catch (error: any) {
            console.error('Error sending email:', error);
            setToastMessage(`Error sending email: ${error.message}`);
            setShowToast(true);
        } finally {
            setUploading(false);
            setSendLoading(false);
        }
    }


    // Templates related state and functions
    const [isTemplatesOpen, setIsTemplatesOpen] = useState(false);
    const [templates, setTemplates] = useState<EmailTemplate[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');

    const categories = ['All', 'Onboarding', 'Security', 'Marketing', 'Support', 'General'];

    const filteredTemplates = templates.filter(template => {
        const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            template.subject.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'All' || template.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    // Fetch templates from Firestore
    useEffect(() => {
        const fetchTemplates = async () => {
            if (!userData) return;
            try {
                const templatesRef = collection(db, `companies/${userData.companyId}/emailTemplates`);
                const templatesQuery = query(templatesRef);
                const querySnapshot = await getDocs(templatesQuery);
                const fetchedTemplates: EmailTemplate[] = [];
                querySnapshot.forEach((doc) => {
                    const data = doc.data() as EmailTemplate;
                    fetchedTemplates.push({
                        id: doc.id,
                        name: data.name,
                        subject: data.subject,
                        content: data.content,
                        category: data.category,
                        createdAt: data.createdAt,
                        lastModified: data.lastModified
                    });
                });
                setTemplates(fetchedTemplates);
            } catch (error) {
                console.error('Error fetching templates:', error);
            }
        };

        fetchTemplates();
    }, [userData]);

    const formatDate = (date: Date | Timestamp) => {

        if (date instanceof Timestamp) {
            date = date.toDate();
        }

        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // handle template click
    const handleTemplateClick = (template: EmailTemplate) => {
        setEmailSubject(template.subject);
        setEmailBody(template.content);
        setIsTemplatesOpen(false);
        setToastMessage(`Loaded template: ${template.name}`);
        setShowToast(true);
    };


    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            {isOpen && (
                <div className="bg-white rounded-3xl w-[80%] xl:w-[50%] max-h-[90vh] overflow-y-auto">
                    <div className="p-6">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-medium">Send Email</h3>
                            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        {/* Area to just show recipients emails in small reactangular boxes */}
                        <div className="flex flex-col">
                            <label className="mb-2 text-sm font-medium text-gray-700">Recipients</label>
                            <div className="flex flex-wrap gap-2 mb-4">
                                {recipients.length === 0 && !manualRecipient && (
                                    <div className="w-full">
                                        <div className="text-gray-500 text-sm mb-2">No recipients added</div>
                                        <button
                                            onClick={() => {
                                                setManualRecipient(true);
                                                setTimeout(() => emailInputRef.current?.focus(), 100);
                                            }}
                                            className="bg-blue-500 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-blue-600"
                                        >
                                            Add Recipient
                                        </button>
                                    </div>
                                )}
                                {recipients.map((recipient, index) => (
                                    <span key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium flex items-center justify-between">
                                        {recipient.email}
                                        <button
                                            onClick={() => {
                                                const newRecipients = recipients.filter((_, i) => i !== index);
                                                setRecipients(newRecipients);
                                                // If this was the last recipient, show manual input
                                                if (newRecipients.length === 0) {
                                                    setManualRecipient(true);
                                                    setTimeout(() => emailInputRef.current?.focus(), 100);
                                                }
                                            }}
                                            className="ml-2 text-red-500 hover:text-red-700"
                                        >
                                            &times;
                                        </button>
                                    </span>
                                ))}
                                {/* add button */}
                                {recipients.length < 5 && recipients.length > 0 && (
                                    <button
                                        onClick={() => {
                                            setManualRecipient(true);
                                            setRecipientEmail('');
                                            // set cursor to input
                                            if (emailInputRef.current) {
                                                setTimeout(() => emailInputRef.current?.focus(), 100);
                                            }
                                        }}
                                        className="bg-blue-500 text-white py-1 px-3 rounded-full text-sm font-medium flex items-center"
                                    >
                                        +
                                    </button>
                                )}
                                {/* remove button */}
                                {/* {recipients.length > 0 && (
                                    <button
                                        onClick={() => {
                                            setRecipients([]);
                                            setManualRecipient(false);
                                        }}
                                        className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium"
                                    >
                                        Remove All
                                    </button>
                                )} */}
                                {/* Manual recipient input */}
                                {manualRecipient && recipients.length < 5 && (
                                    <div className="relative w-full">
                                        <input
                                            type="text"
                                            ref={emailInputRef}
                                            value={recipientEmail}
                                            onChange={(e) => setRecipientEmail(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            onFocus={() => setManualRecipient(true)}
                                            className="border rounded p-2 w-full mb-4 pr-8"
                                            placeholder="Enter email address and press Enter"
                                            maxLength={100}
                                        />
                                        <button
                                            onClick={() => {
                                                setManualRecipient(false);
                                                setRecipientEmail('');
                                            }}
                                            className="absolute right-2 top-2 text-gray-500 hover:text-gray-700 text-lg font-bold"
                                            title="Close email input"
                                        >
                                            &times;
                                        </button>
                                    </div>
                                )}
                            </div>
                            {/* <div className="flex items-center space-x-3 mb-4">
                                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
                                    {recipients.length} / 10
                                </span>
                            </div> */}
                            <label className="mb-2 text-sm font-medium text-gray-700">Subject</label>
                            <input
                                type="text"
                                value={emailSubject}
                                onChange={(e) => setEmailSubject(e.target.value)}
                                className="border rounded p-2 w-full mb-4"
                                placeholder="Enter email subject"
                                maxLength={100}
                            />
                            <div className="flex items-center space-x-3 mb-4">
                                <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">
                                    {emailSubject.length} / 100
                                </span>
                            </div>
                            <label className="mb-2 text-sm font-medium text-gray-700">Body</label>
                            <textarea
                                value={emailBody}
                                onChange={(e) => setEmailBody(e.target.value)}
                                className="border rounded p-2 w-full mb-4"
                                placeholder="Enter email body"
                                rows={10}
                                maxLength={1500}
                                style={{ resize: 'none' }}
                            ></textarea>
                            <div className="flex items-center space-x-3 mb-4">
                                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                                    {emailBody.length} / 1500
                                </span>
                                <div className="flex items-center cursor-pointer" title='max 20 MB per file'>
                                    {/* Hidden file input */}
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleFileSelection}
                                        className="hidden" // This hides the input
                                        accept=".jpg,.jpeg,.png,.pdf,.doc,.docx" // You can customize accepted formats
                                        multiple
                                    />
                                    <div onClick={handleIconClick} className="cursor-pointer p-2 hover:bg-gray-100 rounded-full">
                                        <img src={`${process.env.PUBLIC_URL}/images/desktop/attachment.svg`} alt="Staff Icon" className='w-5 h' />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Area for attachments */}
                        {attachments.length > 0 && (
                            <div className="mb-6">
                                <label className="block text-sm text-gray-600 mb-2">
                                    Attachments
                                    {/* count of attachments */}
                                    <span className="text-xs text-gray-500 ml-2">({attachments.length} file{attachments.length > 1 ? 's' : ''})</span>
                                </label>
                                <div className="grid grid-cols-4 items-center gap-2">
                                    {attachments.map((attachment, index) => (
                                        <div key={index} className="flex items-center justify-between bg-gray-100 rounded-lg p-2">
                                            <img src={`${process.env.PUBLIC_URL}/images/desktop/attachment.svg`} alt="Attachment" className="w-5 h-5 mr-2" />
                                            <span className="text-sm truncate max-w-xs text-gray-700" onClick={
                                                () => {
                                                    // Open the file in a new tab
                                                    const base64String = `data:${attachment.mimeType};base64,${attachment.content}`;
                                                    const link = document.createElement('a');
                                                    link.href = base64String;
                                                    link.download = attachment.filename;
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                }
                                            }
                                            >
                                                {attachment.filename}</span>
                                            <button
                                                onClick={() => handleAttachmentRemove(index)}
                                                className="ml-2 text-red-500 hover:text-red-700"
                                            >
                                                &times;
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Area for buttons */}
                        <div className="flex justify-end space-x-3 mt-6">
                            {/* Button for Email Templates */}
                            <button
                                onClick={() => setIsTemplatesOpen(true)}
                                className={`px-6 py-2 text-white rounded-full bg-rose-600 hover:bg-rose-700 flex items-center`}
                            >
                                Use Templates
                                <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="#FFFFFF"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                            </button>
                            {/* Button for sending email */}
                            <GradientSubmitButton
                                loading={sendLoading}
                                onClick={handleSendEmail}
                                label={uploading ? 'Uploading...' : 'Send Email'}
                                loadingLabel="Sending..."
                                disabled={!emailSubject.trim() || !emailBody.trim() || recipients.length === 0 || uploading}
                                className="px-6 py-2 text-white rounded-full bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                            <button
                                onClick={onClose}
                                className="px-6 py-2 text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Templates Modal */}
            {isTemplatesOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                    <div className="bg-white rounded-3xl w-[80%] xl:w-[50%] max-h-[90vh] overflow-y-auto">
                        {/* Filters */}
                        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                            <div className="flex flex-col md:flex-row gap-4">
                                <div className="flex-1">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                                        <input
                                            type="text"
                                            placeholder="Search templates..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                </div>
                                <select
                                    value={selectedCategory}
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    {categories.map(category => (
                                        <option key={category} value={category}>{category}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Templates List */}
                        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-xl font-semibold text-gray-900">
                                    Templates ({filteredTemplates.length})
                                </h2>
                            </div>

                            {filteredTemplates.length === 0 ? (
                                <div className="p-12 text-center">
                                    <Mail className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
                                    <p className="text-gray-600">
                                        {searchTerm || selectedCategory !== 'All'
                                            ? 'Try adjusting your search or filter criteria'
                                            : 'Create your first email template to get started'}
                                    </p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-200">
                                    {filteredTemplates.map(template => (
                                        <div key={template.id} className="p-6 hover:bg-gray-50 transition-colors"
                                            onClick={() => handleTemplateClick(template)}
                                        >
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                                                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                                            {template.category}
                                                        </span>
                                                    </div>
                                                    <p className="text-gray-600 mb-2 font-medium">{template.subject}</p>
                                                    <div className="text-sm text-gray-500 mb-3">
                                                        {template.content.substring(0, 150)}
                                                        {template.content.length > 150 && '...'}
                                                    </div>
                                                    <div className="flex items-center gap-4 text-sm text-gray-500">
                                                        <div className="flex items-center gap-1">
                                                            <Calendar className="h-4 w-4" />
                                                            Created: {formatDate(template.createdAt)}
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <Calendar className="h-4 w-4" />
                                                            Modified: {formatDate(template.lastModified)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex justify-end space-x-3 p-6">
                            <button
                                onClick={() => setIsTemplatesOpen(false)}
                                className="px-6 py-2 text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50"
                            >
                                Back
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showToast && (
                <Toast
                    message={toastMessage}
                    isError={
                        toastMessage.includes('Error') ||
                        toastMessage.includes('Cannot') ||
                        toastMessage.includes('Failed')
                    }
                    show={showToast}
                    onClose={() => setShowToast(false)}
                />
            )}

        </div>
    )
}

export default EmailModal
